import React, { useState, useCallback } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  Platform,
  useWindowDimensions,
} from 'react-native';
import * as Haptics from 'expo-haptics';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store';
import { BlurView } from 'expo-blur';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as Crypto from 'expo-crypto';
import * as LocalAuthentication from 'expo-local-authentication';
import { useTheme } from '../hooks/useTheme';
import UserAvatar from './UserAvatar';
import Icon from './Icon';
import { SET_LOCKED } from '../store/constants';
import { signOut } from '../store/actions/auth';
import { authenticateWithPasskey, isPasskeySupported } from '../utilities/passkeys';
import { isBiometricEnabled } from '../utilities/biometricStore';
import { fetchUser } from '../store/actions/user';
import { getElectronBridge, DESKTOP_BREAKPOINT } from '../utilities/platform';
import { hexToRgba } from '../utilities/color';
import type { RootState } from '../store';

// Desktop widget frame — mirrors DesktopLayout's gridding so the lock screen
// reads as part of the same surface (centered rounded card on a brand-tinted
// backdrop) rather than an edge-to-edge takeover that breaks the visual model.
const DESKTOP_WIDGET_MAX_WIDTH = 480;

// Delay before the auto-biometric prompt fires after the lock screen mounts.
// On Mac, `bridge.promptBiometrics()` opens the system Touch ID dialog which
// steals focus from the Electron renderer and can cancel the in-flight avatar
// `<img>` request. Giving the avatar a head start lets the cached/fresh image
// land before the prompt interrupts it. Native iOS Face ID is fast enough not
// to need this, but the 400ms is unnoticeable to users either way.
const AUTO_BIOMETRIC_DELAY_MS = 400;

// Ceiling on a single unlock attempt. Longer than cognitoFetch's own 20s
// deadline so a genuine network error surfaces with its specific message and
// this only fires for an await that has no deadline of its own.
const UNLOCK_TIMEOUT_MS = 30_000;

const INACTIVITY_TIMEOUT_MINUTES = 10;

// ─── PIN dots (iOS-style) ─────────────────────────────────────────────────────

const PIN_LENGTH = 4;
const PAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'];

interface PinDotsProps {
  length: number;
  filled: number;
  shake: boolean;
  theme: any;
}

function PinDots({ length, filled, shake, theme }: PinDotsProps) {
  return (
    <View
      style={pinDotsStyle.row}
      accessible
      accessibilityLabel="PIN entry"
      accessibilityValue={{ text: `${filled} of ${length} digits entered` }}
    >
      {Array.from({ length }).map((_, i) => (
        <View
          key={i}
          style={[
            pinDotsStyle.dot,
            {
              backgroundColor: i < filled ? (theme.colors.text || '#000') : 'transparent',
              borderColor: i < filled ? (theme.colors.text || '#000') : theme.colors.muted,
              opacity: i < filled ? 1 : 0.4,
              transform: shake ? [{ translateX: (i % 2 === 0 ? -4 : 4) }] : [],
            },
          ]}
        />
      ))}
    </View>
  );
}

const pinDotsStyle = StyleSheet.create({
  row: { flexDirection: 'row', gap: 20, marginBottom: 32, justifyContent: 'center' },
  dot: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2,
  },
});

// ─── PIN pad (iOS-style 3x4 grid, large touch targets) ────────────────────────

interface PinPadProps {
  onPress: (key: string) => void;
  theme: any;
  onBiometric?: () => void;
  biometricIcon?: any;
  biometricLabel?: string;
}

function PinPad({ onPress, theme, onBiometric, biometricIcon, biometricLabel }: PinPadProps) {
  const keyBg = Platform.OS === 'ios'
    ? (theme.colors.surface || 'rgba(120,120,128,0.18)')
    : theme.colors.surface;
  const keyTextColor = theme.colors.text;
  const deleteColor = theme.colors.muted;

  return (
    <View style={pinPadStyle.grid}>
      {PAD_KEYS.map((key, i) => (
        <View key={i} style={pinPadStyle.cell}>
          {key === '' ? (
            onBiometric ? (
              <TouchableOpacity
                style={[pinPadStyle.key, { backgroundColor: 'transparent' }]}
                onPress={onBiometric}
                accessibilityRole="button"
                accessibilityLabel={biometricLabel}
                activeOpacity={0.7}
              >
                <Icon icon={biometricIcon || ['fad', 'face-viewfinder']} size={28} color={deleteColor} />
              </TouchableOpacity>
            ) : null
          ) : (
            <TouchableOpacity
              style={[
                pinPadStyle.key,
                key === '⌫'
                  ? { backgroundColor: 'transparent' }
                  : { backgroundColor: keyBg },
              ]}
              onPress={() => onPress(key)}
              accessibilityRole="button"
              accessibilityLabel={key === '⌫' ? 'Delete last digit' : key}
              activeOpacity={0.7}
            >
              {key === '⌫' ? (
                <Icon icon={['fas', 'delete-left']} size={24} color={deleteColor} />
              ) : (
                <Text style={[pinPadStyle.keyText, { color: keyTextColor }]}>{key}</Text>
              )}
            </TouchableOpacity>
          )}
        </View>
      ))}
    </View>
  );
}

const pinPadStyle = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: 280,
    justifyContent: 'center',
    gap: 24,
  },
  cell: {
    width: 76,
    minHeight: 76,
    alignItems: 'center',
    justifyContent: 'center',
  },
  key: {
    width: 76,
    minHeight: 76,
    paddingVertical: 8,
    borderRadius: 38,
    alignItems: 'center',
    justifyContent: 'center',
  },
  keyText: {
    fontSize: 28,
    fontWeight: '300',
  },
});

// ─── LockScreen ───────────────────────────────────────────────────────────────

const LockScreen = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const styles = makeStyles(theme);

  const user = useSelector((state: RootState) => state.authentication.user);
  const account = useSelector((state: RootState) => state.session.account);
  const locked = useSelector((state: RootState) => state.session.locked);
  const themeMode = useSelector((state: RootState) => state.session.theme);

  const insets = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  // Desktop widget frame kicks in at the same breakpoint as DesktopLayout so
  // the lock screen visually nests inside the same shell the rest of the app
  // uses on web/Electron — no edge-to-edge takeover that breaks the gridding.
  const isDesktopWidget = Platform.OS === 'web' && windowWidth >= DESKTOP_BREAKPOINT;
  const hasPin = !!user?.pin;
  const allPasskeys: any[] = Array.isArray(user?.passkeys) ? user.passkeys : [];
  // On web, only count passkeys registered against the current origin.
  // A passkey created on app.eleplan.com cannot unlock development.eleplan.org
  // (WebAuthn rpId is bound to origin), so showing "Use fingerprint" while no
  // origin-matching passkey exists just dead-ends the user. On native the
  // origin field is meaningless — every passkey is good for app.eleplan.com.
  const currentOrigin = (typeof window !== 'undefined' && window.location?.origin) || '';
  // Only passkeys this runtime can exercise count. Electron cannot run
  // WebAuthn (see isPasskeySupported), so a registered passkey there would
  // only put up a "Use Passkey" button whose prompt never settles; with none
  // usable the screen falls back to the PIN pad like any other passkey-less
  // device.
  const passkeys: any[] = !isPasskeySupported()
    ? []
    : Platform.OS === 'web' && currentOrigin
      ? allPasskeys.filter((p: any) => !p?.origin || p.origin === currentOrigin)
      : allPasskeys;
  const firstName = user?.first_name || 'there';

  const [mode, setMode] = useState<'pin' | 'biometric'>(hasPin ? 'pin' : 'biometric');
  const [pinInput, setPinInput] = useState('');
  const [shake, setShake] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [hasBiometric, setHasBiometric] = useState(false);
  // Tracks Electron-side Touch ID hardware availability. `bridge.biometricsAvailable()`
  // returns true only on darwin where Electron's systemPreferences.canPromptTouchID()
  // resolves true — Windows Hello / Linux are not yet wired in main.ts. We keep this
  // separate from `hasBiometric` so the icon/label can show Touch ID specifically and
  // `handleBiometric` knows to route through the bridge instead of expo-local-auth
  // (which is stubbed to always fail on web/electron via the webpack alias).
  const [hasElectronBiometric, setHasElectronBiometric] = useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const [enabled, electronAvail] = await Promise.all([
        isBiometricEnabled().catch(() => false),
        (async () => {
          const bridge = getElectronBridge();
          if (!bridge?.biometricsAvailable) return false;
          try { return !!(await bridge.biometricsAvailable()); } catch { return false; }
        })(),
      ]);
      if (cancelled) return;
      setHasElectronBiometric(electronAvail);
      // Treat Electron Touch ID as biometric for downstream UX gates. The
      // `isBiometricEnabled` AsyncStorage flag is a mobile concept (it gates
      // biometric LOGIN via the keychain refresh token) and shouldn't apply
      // on Mac where Touch ID is just an OS-level convenience for unlock.
      setHasBiometric(enabled || electronAvail);
    })();
    return () => { cancelled = true; };
  }, []);

  // Sync mode → 'pin' once we know there's no usable biometric method.
  // The initial state can land on 'biometric' (e.g. user.pin not yet
  // hydrated when LockScreen mounts), and on RN-Web with no Touch ID and
  // no origin-matching passkey, the "Use fingerprint" button dead-ends.
  // Falling back to PIN keeps the user from getting stuck staring at a
  // button that does nothing. Native flows still default to biometric
  // because the OS prompt always works.
  React.useEffect(() => {
    if (!hasPin) return;
    if (mode !== 'biometric') return;
    const noUsableBiometric = !hasBiometric && !hasElectronBiometric && passkeys.length === 0;
    if (noUsableBiometric) setMode('pin');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPin, hasBiometric, hasElectronBiometric, passkeys.length]);

  // Declared up here so the auto-trigger effect below can reference it without
  // the no-use-before-define lint fighting us; the actual ref is read after
  // handleBiometric is defined further down.
  const autoTriggeredRef = React.useRef(false);

  // ─── Unlock helpers ─────────────────────────────────────────────────────────

  const doUnlock = useCallback(async () => {
    await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    setSuccess(true);
    // Fire-and-forget, NOT awaited. This is a refresh of data the app already
    // holds — it has no bearing on whether the person just proved who they
    // are, and the unlock below must not wait on the network to find out.
    // Awaited, a stalled request (see cognitoFetch: a socket that opens and
    // goes quiet never settles) left an already-authenticated user staring at
    // a locked screen indefinitely. Failure is swallowed for the same reason:
    // stale user data is not a reason to refuse entry.
    if (user?.cognito_id && account?.account_id) {
      Promise.resolve(dispatch(fetchUser(user.cognito_id, account.account_id)))
        .catch(() => { /* refresh is best-effort; the unlock already happened */ });
    }
    // Clear Electron main's `isLocked` flag in addition to the Redux flag.
    // The Touch ID path already does this implicitly because
    // `bridge.promptBiometrics()` calls `unlockApp()` in main on success,
    // but the PIN-only path doesn't go through the bridge — without this
    // call the main process keeps `isLocked = true`, which then permanently
    // short-circuits future `lockApp()` calls (Cmd+L, idle timer, OS sleep)
    // because they all bail on `if (isLocked) return;`.
    try { getElectronBridge()?.unlockScreen?.(); } catch { /* native: no-op */ }
    setTimeout(() => {
      dispatch({ type: SET_LOCKED, payload: { show: false, inactive: false } });
      setSuccess(false);
      setVerifying(false);
      setPinInput('');
    }, 500);
  }, [user, account, dispatch]);

  const doError = useCallback((msg: string) => {
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
    setError(msg);
    setShake(true);
    setTimeout(() => { setShake(false); setError(''); setPinInput(''); }, 800);
    setVerifying(false);
  }, []);

  // ─── PIN flow ────────────────────────────────────────────────────────────────

  const handlePinKey = useCallback(async (key: string) => {
    if (verifying || success) return;

    if (key === '⌫') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      setPinInput((p) => p.slice(0, -1));
      return;
    }

    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    const next = pinInput + key;
    setPinInput(next);

    if (next.length < PIN_LENGTH) return;

    // Full PIN entered — verify
    setVerifying(true);
    try {
      const hashed = await Crypto.digestStringAsync(
        Crypto.CryptoDigestAlgorithm.SHA256,
        `${user?.cognito_id}:${next}`,
      );
      if (hashed === user?.pin) {
        await doUnlock();
      } else {
        doError('Incorrect PIN. Try again.');
      }
    } catch {
      doError('Verification failed. Try again.');
    }
  }, [pinInput, verifying, success, user?.pin, user?.cognito_id, doUnlock, doError]);

  // ─── Biometric / passkey flow ────────────────────────────────────────────────

  const handleBiometric = useCallback(async () => {
    if (verifying) return;
    setVerifying(true);
    setError('');

    // Hard deadline on the whole attempt. Every branch below eventually calls
    // setVerifying(false) — but only on paths where its awaits SETTLE, and the
    // unlock button is `disabled={verifying}`. So a single promise that never
    // settles disables the only control on the screen forever, with no error
    // and no route back to the PIN pad. That is the failure this guards: on
    // resume from sleep the token refresh behind the passkey challenge could
    // stall, and the lock screen would spin until the user signed out.
    //
    // Deliberately longer than the underlying fetch deadline so a real network
    // error still wins the race and keeps its specific message; this only
    // catches the case where something below has no deadline of its own.
    const deadline = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('unlock-timeout')), UNLOCK_TIMEOUT_MS),
    );

    const attempt = async (): Promise<void> => {
      let authed = false;

      // Electron desktop: Mac Touch ID is exposed via the preload bridge as
      // `promptBiometrics`. expo-local-authentication is stubbed to always
      // resolve `{ success: false }` on web/electron (see webpack.config.js
      // alias), so we'd never trigger the sensor without going through the
      // bridge directly. The bridge resolves `not-available` on Windows /
      // Linux today; we just fall through to the existing flow in that case.
      const bridge = getElectronBridge();
      if (bridge?.promptBiometrics && hasElectronBiometric) {
        try {
          const res = await bridge.promptBiometrics('unlock Eleplan');
          authed = !!res?.success;
        } catch {
          // Bridge threw — leave authed=false and fall through to other paths.
        }
        if (authed) {
          await doUnlock();
          return;
        }
      }

      // Check biometric state once up-front so all branches below can reference it.
      const biometricOn = await isBiometricEnabled();

      // Prefer biometric (Face ID / fingerprint) for unlock — it's the quiet,
      // one-look gesture iOS users expect after backgrounding. We intentionally
      // do NOT run the passkey flow first, because iOS's ASAuthorization UI
      // shows a "Choose a passkey" sheet AND runs Face ID inside it, which
      // reads as a confusing double-prompt for what should be a single unlock.
      // Passkey stays available as a fallback below when biometric isn't
      // configured, so passkey-only users can still get in.
      if (biometricOn) {
        // disableDeviceFallback: true is critical on Android. With the default
        // (false), if the biometric scan is cancelled or fails twice, expo
        // falls through to the device's PIN/pattern; on Android a recently
        // unlocked phone can satisfy that fallback without further input,
        // letting the user dismiss what looks like the biometric prompt and
        // end up inside the app. The in-app PIN pad below the biometric icon
        // is the intended fallback. (ED-2045)
        const res = await LocalAuthentication.authenticateAsync({
          promptMessage: 'Unlock Eleplan',
          cancelLabel: 'Cancel',
          disableDeviceFallback: true,
        });
        authed = res.success === true;
      }

      // Passkey fallback: only when biometric isn't set up. Avoids the
      // double-prompt on devices that have both configured.
      if (!authed && !biometricOn && passkeys.length > 0 && isPasskeySupported()) {
        authed = await authenticateWithPasskey(passkeys);
      }

      // Raw device auth: only when NEITHER passkeys NOR biometrics are configured.
      if (!authed && passkeys.length === 0 && !biometricOn) {
        const res = await LocalAuthentication.authenticateAsync({
          promptMessage: 'Unlock Eleplan',
          fallbackLabel: 'Use PIN',
          disableDeviceFallback: false,
        });
        authed = res.success;
      }

      if (authed) {
        await doUnlock();
        return;
      }
      throw new Error('not-authenticated');
    };

    try {
      await Promise.race([attempt(), deadline]);
    } catch (err: any) {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
      // A timeout is a different story from a rejected fingerprint, and telling
      // the user "Authentication failed" when the network stalled sends them to
      // re-scan a finger that was never the problem.
      setError(
        err?.message === 'unlock-timeout'
          ? 'Taking too long — check your connection, or enter your PIN.'
          : 'Authentication failed. Please try again.',
      );
      // The screen is usable again regardless of WHY the attempt ended: the
      // button re-enables and the PIN fallback is reachable. Nothing below this
      // line may depend on the network.
      setVerifying(false);
      setTimeout(() => setError(''), 2500);
    }
  }, [verifying, passkeys, hasElectronBiometric, doUnlock]);

  // Auto-fire Face ID / fingerprint as soon as the lock screen appears in
  // biometric mode. Matches the iOS expectation that returning to a
  // backgrounded app glances the sensor immediately instead of waiting for a
  // tap. Gated on `locked.show` (only while the overlay is visible),
  // `mode === 'biometric'` (PIN-pad users clearly want to type), and
  // `hasBiometric` (don't auto-pop "Authentication failed" for users who only
  // configured a passkey / PIN). `autoTriggeredRef` prevents re-firing on
  // every re-render while the OS prompt is up.
  //
  // We delay the actual call by ~400ms (see AUTO_BIOMETRIC_DELAY_MS) so the
  // avatar's network fetch lands before the Touch ID prompt steals focus —
  // otherwise the in-flight <img> request gets cancelled and the avatar
  // latches to initials.
  React.useEffect(() => {
    if (!locked?.show) {
      autoTriggeredRef.current = false;
      return;
    }
    if (
      mode === 'biometric' &&
      hasBiometric &&
      !autoTriggeredRef.current &&
      !verifying &&
      !success
    ) {
      autoTriggeredRef.current = true;
      const t = setTimeout(handleBiometric, AUTO_BIOMETRIC_DELAY_MS);
      return () => clearTimeout(t);
    }
  }, [locked?.show, mode, hasBiometric, verifying, success, handleBiometric]);

  // ─── Guard — all hooks must be above this ────────────────────────────────────

  const required = [user?.first_name, user?.last_name, user?.cognito_id, user?.email];
  if (!locked?.show) return null;

  // ED-2045: when the lock is on but the user record hasn't fully hydrated yet
  // (e.g., the avatar/name fetch is still in flight after sign-in), we MUST
  // still cover the app behind us — the auto-biometric useEffect above will
  // fire the OS prompt regardless of what we render, and on Android Trust
  // Agent can satisfy the biometric silently while the user is staring at an
  // empty screen. Previously we returned null here, which left the app
  // accessible behind the prompt — cancelling or auto-succeeding the biometric
  // dropped the user straight into the app without auth.
  if (!required.every((f) => f?.length)) {
    return (
      <Modal visible transparent animationType="none" statusBarTranslucent>
        <View
          style={[
            StyleSheet.absoluteFillObject,
            {
              backgroundColor: theme.colors.background,
              alignItems: 'center',
              justifyContent: 'center',
            },
          ]}
        >
          <ActivityIndicator color={theme.colors.primary} size="large" />
        </View>
      </Modal>
    );
  }

  const handleSignOut = async () => {
    // Mirror doUnlock: also reset Electron main's `isLocked` so a future
    // Cmd+L / idle / system-lock can fire correctly after re-authentication.
    try { getElectronBridge()?.unlockScreen?.(); } catch { /* native: no-op */ }
    dispatch({ type: SET_LOCKED, payload: { show: false, inactive: false } });
    await dispatch(signOut());
  };

  // Label/icon reflect the PRIMARY method handleBiometric will run. When
  // biometric is configured we run Face ID / Touch ID / fingerprint directly;
  // passkey is only used as a fallback for users without biometric, so the
  // button should only advertise "Use Passkey" in that specific case.
  // Electron Mac is its own branch — `bridge.biometricsAvailable()` only
  // resolves true on darwin where Touch ID exists, so we surface it as
  // "Use Touch ID" with the fingerprint icon.
  const biometricLabel = hasElectronBiometric
    ? 'Use Touch ID'
    : hasBiometric
      ? (Platform.OS === 'ios' ? 'Use Face ID' : 'Use fingerprint')
      : passkeys.length > 0
        ? 'Use Passkey'
        : (Platform.OS === 'ios' ? 'Use Face ID' : 'Use fingerprint');

  const biometricIcon: any = hasElectronBiometric
    ? ['fad', 'fingerprint']
    : hasBiometric
      ? (Platform.OS === 'ios' ? ['fad', 'face-viewfinder'] : ['fad', 'fingerprint'])
      : passkeys.length > 0
        ? ['fad', 'fingerprint']
        : (Platform.OS === 'ios' ? ['fad', 'face-viewfinder'] : ['fad', 'fingerprint']);

  const subtitleText = error || (locked?.inactive
    ? `Locked after ${INACTIVITY_TIMEOUT_MINUTES} min of inactivity`
    : 'Eleplan is locked');

  // Desktop backdrop mirrors DesktopLayout's brand-tinted gradient so the
  // gridding stays consistent when the lock overlays the app. On iOS we keep
  // the BlurView; on phone-width web the existing solid scrim.
  const electricBlue = (theme.colors as any).electricBlue || '#06B6D4';
  const desktopBackdropStyle = isDesktopWidget ? ({
    backgroundImage: `linear-gradient(135deg, ${hexToRgba(electricBlue, 0.10)} 0%, ${theme.colors.background} 55%, ${theme.colors.background} 100%)`,
  } as any) : null;

  return (
    // Wrapping in <Modal> (not a plain absolute-fill View) is what guarantees
    // the lock screen is the top-most layer. React Native's Modal — and its
    // react-native-web equivalent (a `createPortal` into `document.body`) —
    // always renders above the regular View tree, so a Modal that's already
    // open when the screen locks (e.g. PlanSetupSheet, BiometricSetupModal,
    // any sheet that uses <Modal>) would otherwise paint on top of the lock
    // overlay. Mounting our own Modal here puts the lock in the same overlay
    // stack and, because it mounts last, on top of anything else open.
    //
    // `transparent` keeps the Modal background fully owned by our BlurView /
    // scrim below; `animationType="none"` matches the previous instant-on
    // behaviour; `onRequestClose` is a no-op so the Android back button
    // can't dismiss the lock without authentication.
    <Modal
      visible
      transparent
      animationType="none"
      statusBarTranslucent
      hardwareAccelerated
      onRequestClose={() => { /* lock is non-dismissible without auth */ }}
    >
      <View style={StyleSheet.absoluteFillObject} pointerEvents="box-none">
        {Platform.OS === 'ios' ? (
          <BlurView
            intensity={90}
            tint={themeMode === 'dark' ? 'dark' : 'light'}
            style={StyleSheet.absoluteFillObject}
          />
        ) : isDesktopWidget ? (
          <View style={[StyleSheet.absoluteFillObject, desktopBackdropStyle]} />
        ) : (
          <View style={[StyleSheet.absoluteFillObject, { backgroundColor: theme.colors.background + 'f5' }]} />
        )}

        <ScrollView
          style={styles.scroll}
          contentContainerStyle={[
            styles.wrapper,
            { paddingTop: insets.top, paddingBottom: insets.bottom + 16 },
            isDesktopWidget && styles.wrapperDesktop,
          ]}
        >
          <View
            style={[
              styles.centerBlock,
              isDesktopWidget && [
                styles.centerBlockDesktop,
                { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
              ],
            ]}
          >
          {/* Avatar — compact, iOS lock style */}
          <View style={[styles.avatarWrap, success && { borderColor: theme.colors.success }]}>
            <UserAvatar
              cognitoId={user?.cognito_id}
              firstName={user?.first_name}
              lastName={user?.last_name}
              hasAvatar={!!user?.avatar}
              updatedAt={user?.updated_at}
              size={80}
            />
          </View>

          <Text style={[styles.title, { color: theme.colors.text }]}>
            {firstName.charAt(0).toUpperCase() + firstName.slice(1)}
          </Text>
          <Text
            style={[
              styles.subtitle,
              { color: error ? theme.colors.error : theme.colors.muted },
            ]}
            numberOfLines={2}
          >
            {subtitleText}
          </Text>

          {mode === 'pin' && (
            <>
              <PinDots length={PIN_LENGTH} filled={pinInput.length} shake={shake} theme={theme} />
              {verifying && !success ? (
                <ActivityIndicator color={theme.colors.text} style={styles.spinner} />
              ) : success ? (
                <View style={styles.successRow}>
                  <Icon icon={['fas', 'check']} size={20} color={theme.colors.success} />
                  <Text style={[styles.successText, { color: theme.colors.success }]}>Unlocked</Text>
                </View>
              ) : (
                <PinPad
                  onPress={handlePinKey}
                  theme={theme}
                  onBiometric={(passkeys.length > 0 || hasBiometric) ? handleBiometric : undefined}
                  biometricIcon={biometricIcon}
                  biometricLabel={biometricLabel}
                />
              )}
            </>
          )}

          {mode === 'biometric' && (
            <>
              <TouchableOpacity
                style={[
                  styles.unlockBtn,
                  { backgroundColor: success ? theme.colors.success : theme.colors.primary },
                ]}
                onPress={handleBiometric}
                disabled={verifying}
                activeOpacity={0.8}
              >
                {verifying ? (
                  <ActivityIndicator color="#fff" size="small" />
                ) : success ? (
                  <View style={styles.unlockInner}>
                    <Icon icon={['fas', 'check']} size={20} color="#fff" />
                    <Text style={styles.unlockText}>Unlocked</Text>
                  </View>
                ) : (
                  <View style={styles.unlockInner}>
                    <Icon icon={biometricIcon} size={22} color="#fff" />
                    <Text style={styles.unlockText}>{biometricLabel}</Text>
                  </View>
                )}
              </TouchableOpacity>

              {hasPin && (
                <TouchableOpacity
                  style={styles.altLink}
                  onPress={() => { setMode('pin'); setError(''); }}
                  hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                >
                  <Text style={[styles.altLinkText, { color: theme.colors.primary }]}>
                    Enter Passcode
                  </Text>
                </TouchableOpacity>
              )}
            </>
          )}

          <TouchableOpacity
            onPress={handleSignOut}
            style={styles.signOutLink}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          >
            <Text style={[styles.signOutText, { color: theme.colors.muted }]}>
              Not {firstName.charAt(0).toUpperCase() + firstName.slice(1)}?{' '}
              <Text style={[styles.signOutHighlight, { color: theme.colors.primary }]}>Sign Out</Text>
            </Text>
          </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
};

// Whether any biometric/passkey method is available for the alt-link
function isBiometricAvailable(passkeys: any[]) {
  return passkeys.length > 0; // passkeys always available if registered; biometric check is async so we default to showing the link
}

export default LockScreen;

const makeStyles = (theme: ReturnType<typeof import('../hooks/useTheme').useTheme>) =>
  StyleSheet.create({
    scroll: {
      flex: 1,
    },
    wrapper: {
      flexGrow: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 32,
    },
    // Desktop overlay sits inside the gridded layout — extra breathing room so
    // the widget card doesn't kiss the gutter, and we keep the same centering.
    wrapperDesktop: {
      paddingHorizontal: 64,
      paddingVertical: 64,
    },
    centerBlock: {
      width: '100%',
      maxWidth: 340,
      alignItems: 'center',
      justifyContent: 'center',
    },
    // Widget card: rounded, hairline-bordered, soft drop shadow on web — the
    // same visual contract as DesktopLayout's widget so the lock screen reads
    // as part of the same surface rather than a separate full-screen takeover.
    centerBlockDesktop: {
      maxWidth: DESKTOP_WIDGET_MAX_WIDTH,
      paddingHorizontal: 40,
      paddingVertical: 48,
      borderRadius: 18,
      borderWidth: StyleSheet.hairlineWidth,
      ...Platform.select({
        web: {
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.18), 0 6px 18px rgba(0, 0, 0, 0.10)',
        } as any,
        default: {},
      }),
    },
    avatarWrap: {
      marginBottom: 20,
      borderRadius: 50,
      borderWidth: 3,
      borderColor: theme.colors.primary
    },
    title: {
      fontSize: 26,
      fontWeight: '600',
      marginBottom: 6,
      textAlign: 'center',
    },
    subtitle: {
      fontSize: 15,
      textAlign: 'center',
      marginBottom: 28,
      lineHeight: 20,
      paddingHorizontal: 8,
    },
    spinner: { marginBottom: 32 },
    successRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      marginBottom: 32,
      minHeight: 76,
    },
    successText: {
      fontSize: 17,
      fontWeight: '600',
    },
    unlockBtn: {
      width: '100%',
      minHeight: 52,
      borderRadius: 26,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 16,
      paddingVertical: 14,
      paddingHorizontal: 24,
    },
    unlockInner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },
    unlockText: {
      color: '#fff',
      fontSize: 17,
      fontWeight: '600',
    },
    altLink: {
      paddingVertical: 14,
      marginTop: 4,
    },
    altLinkText: {
      fontSize: 16,
      fontWeight: '500',
    },
    signOutLink: {
      paddingVertical: 14,
      marginTop: 24,
    },
    signOutText: {
      fontSize: 15,
      textAlign: 'center',
    },
    signOutHighlight: {
      fontWeight: '600',
    },
  });
